/**
 * Before/after diffs for audit logging of order and bill mutations.
 * Pure — no Firebase imports, shared by POS, functions and the future VPS API.
 *
 * Every diff returns a signed `amountDelta` so the audit log can be summed as a
 * ledger (e.g. total value voided per staff member per shift).
 */

import type { AuditChange } from './auditLogs'

export interface AuditLineLike {
  id?: string
  foodId: string
  quantity: number
  optionsSelected?: string[]
  displayName?: string
  unitPrice?: number
  cancelledAt?: string | null
}

export interface AuditAdjustmentLike {
  id: string
  label: string
  amount: number
}

export interface AuditDiffResult {
  changes: AuditChange[]
  /** Signed money impact: negative means the bill got cheaper. */
  amountDelta: number
  /** Short human phrase for the log message. Empty when nothing changed. */
  summary: string
}

export interface OrderLinesDiff extends AuditDiffResult {
  addedCount: number
  removedCount: number
  changedCount: number
}

function isActive(line: AuditLineLike): boolean {
  return line.cancelledAt == null || line.cancelledAt === ''
}

function qtyOf(line: AuditLineLike): number {
  return Number.isFinite(line.quantity) && line.quantity > 0 ? line.quantity : 0
}

function unitPriceOf(line: AuditLineLike): number {
  return line.unitPrice != null && Number.isFinite(line.unitPrice) ? Number(line.unitPrice) : 0
}

/** Value of a line for ledger purposes. Cancelled lines are worth zero. */
export function auditLineAmount(line: AuditLineLike): number {
  if (!isActive(line)) return 0
  return unitPriceOf(line) * qtyOf(line)
}

export function auditLineLabel(line: AuditLineLike): string {
  return line.displayName?.trim() || line.foodId
}

function optionsKey(line: AuditLineLike): string {
  return [...(line.optionsSelected ?? [])].sort().join(' | ')
}

function describeLine(line: AuditLineLike): string {
  const parts = [`x${qtyOf(line)}`]
  const unit = unitPriceOf(line)
  if (unit > 0) parts.push(`@ ${unit}`)
  const options = optionsKey(line)
  if (options) parts.push(`(${options})`)
  return parts.join(' ')
}

/**
 * Match key for pairing prev/next lines. Stable line ids are authoritative; lines
 * without one (legacy rows) fall back to their food + options identity.
 */
function matchKey(line: AuditLineLike, index: number): string {
  if (line.id != null && line.id !== '') return `id:${line.id}`
  return `fallback:${line.foodId}:${optionsKey(line)}:${index}`
}

function indexLines(lines: readonly AuditLineLike[]): Map<string, AuditLineLike> {
  const map = new Map<string, AuditLineLike>()
  lines.forEach((line, i) => {
    map.set(matchKey(line, i), line)
  })
  return map
}

function sumAmount(lines: readonly AuditLineLike[]): number {
  return lines.reduce((total, line) => total + auditLineAmount(line), 0)
}

function joinSummary(parts: readonly string[]): string {
  return parts.filter((p) => p.length > 0).join(', ')
}

/**
 * Line-level diff of an order edit: additions, removals, quantity and option
 * changes, with the net money impact.
 */
export function diffOrderLines(
  prevLines: readonly AuditLineLike[],
  nextLines: readonly AuditLineLike[],
): OrderLinesDiff {
  const prevByKey = indexLines(prevLines)
  const nextByKey = indexLines(nextLines)
  const changes: AuditChange[] = []
  let addedCount = 0
  let removedCount = 0
  let changedCount = 0

  for (const [key, prev] of prevByKey) {
    const next = nextByKey.get(key)
    if (!next) {
      changes.push({
        field: auditLineLabel(prev),
        from: describeLine(prev),
        to: null,
      })
      removedCount += 1
      continue
    }

    let lineChanged = false
    if (qtyOf(prev) !== qtyOf(next)) {
      changes.push({
        field: `${auditLineLabel(prev)} qty`,
        from: qtyOf(prev),
        to: qtyOf(next),
      })
      lineChanged = true
    }
    if (optionsKey(prev) !== optionsKey(next)) {
      changes.push({
        field: `${auditLineLabel(prev)} options`,
        from: optionsKey(prev) || null,
        to: optionsKey(next) || null,
      })
      lineChanged = true
    }
    if (unitPriceOf(prev) !== unitPriceOf(next)) {
      changes.push({
        field: `${auditLineLabel(prev)} unit price`,
        from: unitPriceOf(prev),
        to: unitPriceOf(next),
      })
      lineChanged = true
    }
    if (isActive(prev) && !isActive(next)) {
      changes.push({
        field: `${auditLineLabel(prev)} cancelled`,
        from: null,
        to: next.cancelledAt ?? 'cancelled',
      })
      lineChanged = true
    }
    if (lineChanged) changedCount += 1
  }

  for (const [key, next] of nextByKey) {
    if (prevByKey.has(key)) continue
    changes.push({
      field: auditLineLabel(next),
      from: null,
      to: describeLine(next),
    })
    addedCount += 1
  }

  const amountDelta = sumAmount(nextLines) - sumAmount(prevLines)
  const summary = joinSummary([
    addedCount > 0 ? `${addedCount} added` : '',
    removedCount > 0 ? `${removedCount} removed` : '',
    changedCount > 0 ? `${changedCount} changed` : '',
  ])

  return { changes, amountDelta, summary, addedCount, removedCount, changedCount }
}

/**
 * Audit payload for lines being voided. `amountDelta` is negative — the value
 * removed from the bill, which is the figure worth reviewing per staff member.
 */
export function cancelledLinesAudit(lines: readonly AuditLineLike[]): AuditDiffResult {
  const changes: AuditChange[] = []
  let lost = 0
  for (const line of lines) {
    // Value the line as it was before the cancel stamp was applied.
    const amount = unitPriceOf(line) * qtyOf(line)
    lost += amount
    changes.push({
      field: `${auditLineLabel(line)} cancelled`,
      from: describeLine(line),
      to: null,
    })
  }
  const summary = lines.length > 0 ? `${lines.length} line(s) voided` : ''
  // `|| 0` avoids persisting -0 when nothing was voided.
  return { changes, amountDelta: -lost || 0, summary }
}

/** Diff of bill extra charges (delivery fee etc.). */
export function diffBillAdjustments(
  prev: readonly AuditAdjustmentLike[] | undefined,
  next: readonly AuditAdjustmentLike[] | undefined,
): AuditDiffResult {
  const prevList = prev ?? []
  const nextList = next ?? []
  const prevById = new Map(prevList.map((a) => [a.id, a]))
  const nextById = new Map(nextList.map((a) => [a.id, a]))
  const changes: AuditChange[] = []

  for (const [id, before] of prevById) {
    const after = nextById.get(id)
    if (!after) {
      changes.push({ field: `charge: ${before.label}`, from: before.amount, to: null })
      continue
    }
    if (before.amount !== after.amount) {
      changes.push({ field: `charge: ${after.label}`, from: before.amount, to: after.amount })
    }
    if (before.label !== after.label) {
      changes.push({ field: 'charge label', from: before.label, to: after.label })
    }
  }
  for (const [id, after] of nextById) {
    if (prevById.has(id)) continue
    changes.push({ field: `charge: ${after.label}`, from: null, to: after.amount })
  }

  const total = (list: readonly AuditAdjustmentLike[]) =>
    list.reduce((sum, a) => sum + (Number.isFinite(a.amount) ? a.amount : 0), 0)
  const amountDelta = total(nextList) - total(prevList)
  const summary = changes.length > 0 ? `${changes.length} charge change(s)` : ''
  return { changes, amountDelta, summary }
}

/** Simple field-level diff for record edits (menu items, settings). */
export function diffRecordFields(
  prev: Record<string, unknown> | undefined,
  next: Record<string, unknown> | undefined,
  fields?: readonly string[],
): AuditChange[] {
  const before = prev ?? {}
  const after = next ?? {}
  const keys = fields ?? [...new Set([...Object.keys(before), ...Object.keys(after)])]
  const changes: AuditChange[] = []
  for (const key of keys) {
    if (!(key in after)) continue
    const from = before[key]
    const to = after[key]
    if (scalarOrNull(from) === scalarOrNull(to)) continue
    changes.push({ field: key, from: scalarOrNull(from), to: scalarOrNull(to) })
  }
  return changes
}

function scalarOrNull(raw: unknown): string | number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') return raw
  if (typeof raw === 'boolean') return raw ? 'true' : 'false'
  if (raw == null) return null
  if (Array.isArray(raw)) return raw.length > 0 ? raw.join(' | ') : null
  return null
}
